import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";

import { useDefaultPasscodeStyles } from "../../../constants/passCodeUtils";
import { NAV_COMPANYS } from "../../../constants/constants";
import { requestDocumentNumbers, clearDocumentFilter, setDocumentFilter, setNavCompany } from "../../../actions/billing";
import { moment } from "../../../utils/LocalizedUtils";

const BillingDocumentSendingFilterComponent = ({
  filter,
  navCompany,
  requestInProgress,
  translate,
  requestFn,
  clearFilterFn,
  setNavCompany,
  setFilterFn,
}) => {
  const pc = useDefaultPasscodeStyles();

  const handleDateChange = (name, date) => {
    setFilterFn({ ...filter, [name]: moment.utc(date) });
  };

  const handleTextChange = name => event => {
    setFilterFn({ ...filter, [name]: event.target.value });
  };

  const handleNavCompanyChange = event => {
    const key = event.target.value;
    setNavCompany(NAV_COMPANYS.find(c => c.key === key) ?? key);
  };

  const handleChange = name => event => {
    setFilterFn({ ...filter, [name]: event.target.checked });
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", width: "100%" }}>
      <DateTimePicker
        ampm={false}
        format="DD.MM.YYYY HH:mm:ss"
        value={filter.fromPostingDate ? moment(filter.fromPostingDate) : null}
        onChange={date => handleDateChange("fromPostingDate", date)}
        slotProps={{
          textField: {
            id: "ledger-entry-start-time",
            label: translate.orderDateFrom,
            sx: [pc.textField, pc.lineExpander],
          },
        }}
      />
      <DateTimePicker
        ampm={false}
        format="DD.MM.YYYY HH:mm:ss"
        value={filter.toPostingDate ? moment(filter.toPostingDate) : null}
        onChange={date => handleDateChange("toPostingDate", date)}
        slotProps={{
          textField: {
            id: "ledger-entry-end-time",
            label: translate.orderDateTo,
            sx: [pc.textField, pc.lineExpander],
          },
        }}
      />
      <Box sx={pc.lineBreak} />
      <TextField
        id="ledger-entry-conference-id"
        label={translate.fromEntryNo}
        sx={[pc.textField, pc.lineExpander]}
        value={filter.fromEntryNo}
        onChange={handleTextChange("fromEntryNo")}
      />
      <TextField
        id="ledger-entry-conference-id"
        label={translate.toEntryNo}
        sx={[pc.textField, pc.lineExpander]}
        value={filter.toEntryNo}
        onChange={handleTextChange("toEntryNo")}
      />
      <Box sx={pc.lineBreak} />
      <FormControl sx={[pc.textField, pc.lineExpander]} variant="outlined">
        <InputLabel id="dispatch-comment-label" htmlFor="dispatch-comment-select">
          Versandbemerkung
        </InputLabel>
        <Select
          native
          labelId="dispatch-comment-label"
          label="Versandbemerkung"
          value={filter.dispatchComment}
          onChange={handleTextChange("dispatchComment")}
          inputProps={{
            name: "dispatchComment",
            id: "dispatch-comment-select",
          }}
        >
          <option aria-label="None" value="" />
          <option value="ONHOLD">ONHOLD</option>
        </Select>
      </FormControl>
      <FormControlLabel
        sx={[pc.textField, pc.lineExpander, { p: 1 }]}
        control={<Checkbox checked={filter.paperOnly} onChange={handleChange("paperOnly")} value="primary" />}
        label={translate.paperOnly}
      />
      <Box sx={pc.lineBreak} />
      <FormControl sx={[pc.textField, pc.lineExpander]} variant="outlined">
        <InputLabel id="nav-company-label" htmlFor="nav-company-select">
          NAV company
        </InputLabel>
        <Select
          native
          labelId="nav-company-label"
          label="NAV company"
          value={navCompany?.key ?? navCompany ?? ""}
          onChange={handleNavCompanyChange}
          inputProps={{
            name: "navCompany",
            id: "nav-company-select",
          }}
        >
          {NAV_COMPANYS.map(company => (
            <option key={company.key} value={company.key}>
              {company.text}
            </option>
          ))}
        </Select>
      </FormControl>
      <Divider />
      <Box sx={{ mt: 1, width: "100%", display: "inline-flex" }}>
        <Box sx={{ flex: 1, width: "10px" }} />
        <Button size="small" onClick={() => clearFilterFn(filter)}>
          {translate.clearButtonFilter}
        </Button>
        <Button
          disabled={requestInProgress}
          variant="contained"
          size="small"
          color="primary"
          onClick={() => requestFn(navCompany, filter)}
        >
          {translate.searchButtonFilter}
        </Button>
      </Box>
    </Box>
  );
};

BillingDocumentSendingFilterComponent.propTypes = {
  filter: PropTypes.object.isRequired,
  navCompany: PropTypes.object.isRequired,
  setNavCompany: PropTypes.func.isRequired,
  requestFn: PropTypes.func.isRequired,
  clearFilterFn: PropTypes.func.isRequired,
  setFilterFn: PropTypes.func.isRequired,
  requestInProgress: PropTypes.bool.isRequired,
  translate: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  filter: state.billing.documentFilter,
  navCompany: state.billing.navCompany,
  requestInProgress: state.billing.requestBillingInProgress,
  translate: state.ui.translate,
});

const mapDispatchToProps = dispatch => ({
  requestFn: (company, filter) => dispatch(requestDocumentNumbers(company.key, filter)),
  clearFilterFn: () => dispatch(clearDocumentFilter()),
  setFilterFn: filter => dispatch(setDocumentFilter(filter)),
  setNavCompany: company => dispatch(setNavCompany(company)),
});

const BillingDocumentSendingFilter = connect(
  mapStateToProps,
  mapDispatchToProps
)(BillingDocumentSendingFilterComponent);

export default BillingDocumentSendingFilter;
